// WebRTC plumbing for the live booth, talking to Cloudflare Stream.
//   publish(): Greg's browser → Stream (WHIP), from the admin Booth tab
//   play():    Stream → every viewer (WHEP), on /live
// Both follow Cloudflare's reference WHIP/WHEP clients: one SDP offer POSTed
// as application/sdp, a 201 with the answer, and a Location to DELETE on stop.
// During Stream's WebRTC beta, WHIP input can only be watched over WHEP.

const ICE_SERVERS = [{ urls: "stun:stun.cloudflare.com:3478" }];

function waitForIce(pc, ms = 1000) {
  if (pc.iceGatheringState === "complete") return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      pc.removeEventListener("icegatheringstatechange", check);
      clearTimeout(timer);
      resolve();
    };
    const check = () => {
      if (pc.iceGatheringState === "complete") done();
    };
    const timer = setTimeout(done, ms);
    pc.addEventListener("icegatheringstatechange", check);
  });
}

async function negotiate(pc, endpoint) {
  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);
  await waitForIce(pc);
  const res = await fetch(endpoint, {
    method: "POST",
    mode: "cors",
    headers: { "content-type": "application/sdp" },
    body: pc.localDescription.sdp,
  });
  if (res.status !== 201) {
    const detail = await res.text().catch(() => "");
    const err = new Error(detail || `Stream returned ${res.status}`);
    err.status = res.status;
    throw err;
  }
  await pc.setRemoteDescription({ type: "answer", sdp: await res.text() });
  const loc = res.headers.get("location");
  return loc ? new URL(loc, endpoint).toString() : null;
}

function teardown(pc, resource) {
  try {
    pc.close();
  } catch {}
  if (resource) fetch(resource, { method: "DELETE", mode: "cors" }).catch(() => {});
}

// Publish a local MediaStream. onState gets the RTCPeerConnection state
// ("connecting" → "connected"; "failed"/"disconnected" if the network drops).
export async function publish(endpoint, stream, { onState } = {}) {
  const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS, bundlePolicy: "max-bundle" });
  pc.addEventListener("connectionstatechange", () => onState?.(pc.connectionState));
  for (const track of stream.getTracks()) {
    pc.addTransceiver(track, { direction: "sendonly" });
  }
  const resource = await negotiate(pc, endpoint);
  // Cap the upload so a so-so home connection stays smooth. 1.5 Mbps is
  // plenty for a 720p camera shot, and less video in flight means shorter
  // freezes when Wi-Fi hiccups (2.5 Mbps froze for seconds at a time, Oct 12).
  for (const sender of pc.getSenders()) {
    if (sender.track?.kind !== "video") continue;
    try {
      const params = sender.getParameters();
      params.encodings = params.encodings?.length ? params.encodings : [{}];
      params.encodings[0].maxBitrate = 1_500_000;
      await sender.setParameters(params);
    } catch {}
  }
  return { pc, stop: () => teardown(pc, resource) };
}

// Play the booth into a <video>. Rejects with err.status 409/404-ish when
// nobody is broadcasting yet — callers retry. With no video, the connection
// comes up on its own and the caller puts session.stream on screen later.
export async function play(endpoint, video, { onState } = {}) {
  const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS, bundlePolicy: "max-bundle" });
  const stream = new MediaStream();
  pc.addTransceiver("video", { direction: "recvonly" });
  pc.addTransceiver("audio", { direction: "recvonly" });
  pc.addEventListener("track", (e) => {
    if (stream.getTracks().some((t) => t.kind === e.track.kind)) return;
    stream.addTrack(e.track);
  });
  pc.addEventListener("connectionstatechange", () => onState?.(pc.connectionState));
  if (video) video.srcObject = stream;
  try {
    const resource = await negotiate(pc, endpoint);
    return { pc, stream, stop: () => teardown(pc, resource) };
  } catch (err) {
    teardown(pc, null);
    throw err;
  }
}

// A moving test card + soft tone, so the whole pipeline can be checked
// without a camera ("Test pattern" in the Booth tab's camera menu).
export function testPattern() {
  const canvas = document.createElement("canvas");
  canvas.width = 1280;
  canvas.height = 720;
  const g = canvas.getContext("2d");
  let raf = 0;
  const draw = (t) => {
    g.fillStyle = "#050d1a";
    g.fillRect(0, 0, 1280, 720);
    const bars = ["#0064c2", "#bd9b60", "#c8102e", "#f4f1ea", "#102641", "#2b8aff", "#d4b67a"];
    bars.forEach((c, i) => {
      g.fillStyle = c;
      g.fillRect(i * (1280 / 7), 470, 1280 / 7, 250);
    });
    g.fillStyle = "#f4f1ea";
    g.font = "600 72px Georgia, serif";
    g.textAlign = "center";
    g.fillText("Greg's Booth · test pattern", 640, 230);
    g.font = "48px ui-monospace, monospace";
    g.fillStyle = "#d4b67a";
    g.fillText(new Date().toLocaleTimeString(), 640, 330);
    const x = 640 + Math.sin(t / 600) * 500;
    g.beginPath();
    g.arc(x, 405, 18, 0, Math.PI * 2);
    g.fillStyle = "#ff4a3d";
    g.fill();
    raf = requestAnimationFrame(draw);
  };
  raf = requestAnimationFrame(draw);
  const video = canvas.captureStream(30);
  const ac = new AudioContext();
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.frequency.value = 440;
  gain.gain.value = 0.04;
  const dest = ac.createMediaStreamDestination();
  osc.connect(gain).connect(dest);
  osc.start();
  const stream = new MediaStream([...video.getVideoTracks(), ...dest.stream.getAudioTracks()]);
  stream.stopPattern = () => {
    cancelAnimationFrame(raf);
    try {
      osc.stop();
      ac.close();
    } catch {}
  };
  return stream;
}
